import type { Permission } from '@/types/auth.types';

/**
 * New business-level permission actions
 * Hierarchy: DELETE ⟹ MANAGE ⟹ VIEW
 */
export const ActionsEnum = {
  VIEW: 'view',
  MANAGE: 'manage',
  DELETE: 'delete',
  /**
   * Legacy GDPR-erasure action. Erasure is now gated on DELETE, so nothing
   * requires this; it stays declared only so a role that still holds an
   * `erase` grant normalizes cleanly instead of silently falling back to VIEW.
   */
  ERASE: 'erase',
} as const;

export type ActionsEnum = (typeof ActionsEnum)[keyof typeof ActionsEnum];

/**
 * Normalize action to new ActionsEnum format
 * Supports both legacy (read/create/update/delete) and new (view/manage/delete) actions
 */
function normalizeAction(action: string): ActionsEnum {
  const lowerAction = action.toLowerCase();

  // Check if it's already a new action
  if (Object.values(ActionsEnum).includes(lowerAction as ActionsEnum)) {
    return lowerAction as ActionsEnum;
  }

  // Map legacy action to new action
  return ActionsEnum.VIEW;
}

/**
 * Check if a given action satisfies the required action based on hierarchy
 * Hierarchy: DELETE ⟹ MANAGE ⟹ VIEW
 * - DELETE implies MANAGE and VIEW
 * - MANAGE implies VIEW
 * - VIEW only implies VIEW
 */
function actionSatisfiesRequirement(
  userAction: ActionsEnum,
  requiredAction: ActionsEnum,
): boolean {
  switch (userAction) {
    case ActionsEnum.DELETE:
      // DELETE is the highest level and implies every other action.
      return true;
    case ActionsEnum.ERASE:
      // Legacy grant; erasure is gated on DELETE now. Satisfies only itself.
      return requiredAction === ActionsEnum.ERASE;
    case ActionsEnum.MANAGE:
      // MANAGE implies VIEW and MANAGE
      return (
        requiredAction === ActionsEnum.VIEW ||
        requiredAction === ActionsEnum.MANAGE
      );
    case ActionsEnum.VIEW:
      // VIEW only implies VIEW
      return requiredAction === ActionsEnum.VIEW;
    default:
      return false;
  }
}

/**
 * Check if user has a specific permission for a resource
 * Supports permission hierarchy: DELETE > MANAGE > VIEW
 */
export function hasPermission(
  permissions: Permission[] | undefined,
  resource: string,
  action: string,
): boolean {
  if (!permissions || !permissions.length) return false;

  const res = resource.toLowerCase();
  const requiredAction = normalizeAction(action);

  // Check all permissions for this resource
  return permissions.some((p) => {
    if (p.resource?.toLowerCase() !== res) return false;
    const userAction = normalizeAction(p.action);
    return actionSatisfiesRequirement(userAction, requiredAction);
  });
}

/**
 * Check if user has ANY of the required permissions
 */
export function hasAnyPermission(
  permissions: Permission[] | undefined,
  required: { resource: string; action: string }[],
): boolean {
  if (!permissions || !permissions.length) return false;
  return required.some((r) => hasPermission(permissions, r.resource, r.action));
}

/**
 * Check if user has ALL of the required permissions
 */
export function hasAllPermissions(
  permissions: Permission[] | undefined,
  required: { resource: string; action: string }[],
): boolean {
  if (!permissions || !permissions.length) return false;
  return required.every((r) =>
    hasPermission(permissions, r.resource, r.action),
  );
}

/**
 * Get permission capabilities for a specific resource
 * Returns boolean flags for view, manage, and delete capabilities
 *
 * @example
 * const { canView, canManage, canDelete } = getResourcePermissions(permissions, 'patients');
 * // canView: true if user can view (has VIEW, MANAGE, or DELETE)
 * // canManage: true if user can create/edit (has MANAGE or DELETE)
 * // canDelete: true if user can delete (has DELETE only)
 */
export function getResourcePermissions(
  permissions: Permission[] | undefined,
  resource: string,
) {
  return {
    // New semantic names
    canView: hasPermission(permissions, resource, ActionsEnum.VIEW),
    canManage: hasPermission(permissions, resource, ActionsEnum.MANAGE),
    canDelete: hasPermission(permissions, resource, ActionsEnum.DELETE),
    /**
     * GDPR permanent erasure. Gated on the same `delete` right as archiving,
     * matching the server. The typed-name confirmation and the archive-first
     * requirement are what guard the action, not a separate permission.
     */
    canErase: hasPermission(permissions, resource, ActionsEnum.DELETE),
  };
}

/**
 * Check if user can view a resource (read-only access)
 * VIEW is implied by MANAGE and DELETE
 */
export function canView(
  permissions: Permission[] | undefined,
  resource: string,
): boolean {
  return hasPermission(permissions, resource, ActionsEnum.VIEW);
}

/**
 * Check if user can manage a resource (create/update)
 * MANAGE is implied by DELETE
 */
export function canManage(
  permissions: Permission[] | undefined,
  resource: string,
): boolean {
  return hasPermission(permissions, resource, ActionsEnum.MANAGE);
}

/**
 * Check if user can delete a resource
 * DELETE is the highest permission level
 */
export function canDeleteResource(
  permissions: Permission[] | undefined,
  resource: string,
): boolean {
  return hasPermission(permissions, resource, ActionsEnum.DELETE);
}
